import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { JourneyLines } from './JourneyLines';
import { CoachingStatus, MovementList, TierBadgeInline } from './CompanionBlocks';
import { NextUnlock } from './NextUnlock';
import { ChevronDown, ChevronRight, AlertTriangle, Trash2 } from 'lucide-react';

const Section = ({ title, subtitle, children, testid }) => (
  <section className="space-y-3" data-testid={testid}>
    <div>
      <h2 className="text-base md:text-lg font-semibold text-white">{title}</h2>
      {subtitle && <p className="text-sm text-white/50 mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </section>
);

const PractitionerNotes = ({ participantId, notes, onChange }) => {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/practitioner/participants/${participantId}/notes`,
        { body: body.trim() });
      setBody('');
      onChange();
    } catch {
      toast.error('Note not saved');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (noteId) => {
    try {
      await axios.delete(`${API}/practitioner/participants/${participantId}/notes/${noteId}`);
      onChange();
    } catch {
      toast.error('That note belongs to another practitioner');
    }
  };

  return (
    <Section
      title="Practitioner notes"
      subtitle="Practitioners and admins only. The participant never sees these."
      testid="companion-notes"
    >
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder="What would help whoever meets them next?"
          className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm
                     text-white placeholder-white/30 focus:outline-none focus:border-cyan-400/60"
          data-testid="note-input"
        />
        <button
          onClick={add}
          disabled={busy || !body.trim()}
          className="rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 px-4 py-1.5
                     text-sm text-white transition-colors duration-200"
          data-testid="note-save-btn"
        >
          Add note
        </button>

        {(notes || []).map((note) => (
          <div key={note.id} className="border-t border-white/10 pt-3" data-testid={`note-${note.id}`}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-xs text-white/50">
                {note.author_name} · {new Date(note.created_at).toLocaleDateString()}
              </span>
              <button
                onClick={() => remove(note.id)}
                className="text-white/30 hover:text-rose-300 transition-colors duration-200"
                data-testid={`note-delete-${note.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="text-sm text-white/80 mt-1 whitespace-pre-line">{note.body}</p>
          </div>
        ))}
      </div>
    </Section>
  );
};

/** The full developmental arc. One component for participant, practitioner and admin. */
export const CompanionFull = ({ data, onReload }) => {
  const [openDetails, setOpenDetails] = useState(null);
  const latest = data.timeline?.[0];
  const viewer = data.viewer || {};
  const person = data.participant || {};
  const displayName = person.full_name || person.email || 'This participant';
  const isSelf = viewer.is_self;

  return (
    <div className="space-y-10 pb-16" data-testid="companion-full">
      <header className="space-y-4" data-testid="companion-hero">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white">Consciousness Progression</h1>
          <p className="text-base md:text-lg text-white/60 mt-2">
            {isSelf ? 'Your developmental journey' : `${displayName}'s developmental journey`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-white/80">{displayName}</span>
          <TierBadgeInline tier={person.tier} membershipType={person.membership_type} />
          {data.needs_attention && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40
                         bg-amber-400/10 px-3 py-1 text-xs text-amber-200"
              data-testid="companion-needs-attention"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              {data.needs_attention.reason}
            </span>
          )}
          {latest && (
            <span className="text-xs text-white/40">
              Last assessment {new Date(latest.submitted_at).toLocaleDateString()}
            </span>
          )}
        </div>
        <CoachingStatus coaching={data.coaching} />

        <NextUnlock />
      </header>

      {latest?.focus_statement && (
        <p className="text-sm md:text-base text-white/70" data-testid="companion-focus-statement">
          {latest.focus_statement}
        </p>
      )}

      <Section
        title="Dimensional continuity"
        subtitle="Each line shows progression where measured; gaps indicate modules that focused elsewhere."
        testid="companion-continuity"
      >
        <JourneyLines dimensions={data.dimensions} axis={data.axis} bands={data.stage_bands} />
      </Section>

      {latest && (
        <Section title="Movement this module" testid="companion-movement">
          <MovementList movements={latest.movements} testid="companion-movement-list" />
        </Section>
      )}

      {latest?.narrative && (
        <Section title="About this module" testid="companion-narrative">
          <p className="text-sm md:text-base text-white/80 whitespace-pre-line rounded-xl
                        border border-white/10 bg-white/[0.03] p-5">
            {latest.narrative}
          </p>
        </Section>
      )}

      <Section title="Assessment history" testid="companion-history">
        <div className="space-y-2">
          {(data.timeline || []).map((entry) => {
            const open = openDetails === entry.attempt_id;
            const Chevron = open ? ChevronDown : ChevronRight;
            return (
              <div
                key={entry.attempt_id}
                className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden"
              >
                <button
                  onClick={() => setOpenDetails(open ? null : entry.attempt_id)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left
                             hover:bg-white/[0.04] transition-colors duration-200"
                  data-testid={`history-row-${entry.location_id}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <Chevron className="w-4 h-4 text-white/40 shrink-0" />
                    <span className="text-sm text-white/90 truncate">{entry.label}</span>
                  </span>
                  <span className="text-xs text-white/50 shrink-0">
                    {entry.overall_score}% · {entry.overall_stage_name}
                  </span>
                </button>
                {open && (
                  <div className="px-4 pb-4 space-y-3" data-testid={`history-detail-${entry.location_id}`}>
                    <div className="text-xs text-white/40">
                      {new Date(entry.submitted_at).toLocaleDateString()}
                      {entry.is_retake ? ` · retake #${entry.attempt_number}` : ''}
                      {entry.trajectory?.trend_label ? ` · ${entry.trajectory.trend_label}` : ''}
                    </div>
                    <p className="text-xs text-white/50">{entry.focus_statement}</p>
                    {entry.narrative && (
                      <p className="text-sm text-white/70 whitespace-pre-line">{entry.narrative}</p>
                    )}
                    <MovementList movements={entry.movements}
                      testid={`history-movement-${entry.location_id}`} />
                    {isSelf && (
                      <Link
                        to={`/dashboard/assessments/results/${entry.attempt_id}`}
                        className="inline-block text-xs text-cyan-300 hover:text-cyan-200"
                        data-testid={`history-open-${entry.location_id}`}
                      >
                        Open the full reading →
                      </Link>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {(data.timeline || []).length === 0 && (
            <p className="text-sm text-white/50" data-testid="companion-history-empty">
              No assessments completed yet.
            </p>
          )}
        </div>
      </Section>

      {data.closing && (
        <Section
          title={data.closing.title}
          subtitle="All six dimensions, from the Baseline to the most recent reading."
          testid="companion-closing"
        >
          <div className="rounded-2xl border border-violet-300/30 bg-violet-400/[0.06] p-6 space-y-5">
            {data.closing.dimensions.map((row) => (
              <div key={row.dimension} data-testid={`companion-closing-${row.dimension}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-white/90">{row.dimension_name}</span>
                  <span className="text-sm text-white/70">
                    {row.from_percentage}% → {row.to_percentage}%
                    <span className={`ml-2 font-semibold ${
                      row.change > 0 ? 'text-emerald-300'
                        : row.change < 0 ? 'text-amber-300' : 'text-white/40'}`}>
                      {row.change > 0 ? '+' : ''}{row.change}
                    </span>
                  </span>
                </div>
                <div className="text-xs text-white/40 mt-0.5">
                  {row.from_stage_name} → {row.to_stage_name}
                  {!row.latest_is_final && ` · last measured at ${row.to_short_label}`}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {viewer.is_practitioner && !isSelf && (
        <PractitionerNotes
          participantId={person.user_id}
          notes={data.notes}
          onChange={onReload}
        />
      )}

      <footer className="pt-6 border-t border-white/10 flex flex-wrap gap-x-4 gap-y-2 text-xs text-white/30">
        <Link to="/terms" className="hover:text-white/60">Terms of Service</Link>
        <Link to="/privacy" className="hover:text-white/60">Privacy Policy</Link>
        <Link to="/community-guidelines" className="hover:text-white/60">Community Guidelines</Link>
      </footer>
    </div>
  );
};

export default CompanionFull;
